import { z } from "zod";
import { PROTOCOL_VERSION } from "./constants";

export { MATCH_FORFEIT_CLOSE_CODE, PROTOCOL_VERSION, QUEUE_TICKET_TTL_MS } from "./constants";
export {
  MATCH_BINARY_SUBPROTOCOL,
  BINARY_REJECT_SEQUENCE_GAP,
  decodeBinaryMatchMessage,
  encodeForfeit,
  encodePlaceItem,
  encodeRemoveItem,
  encodeStateRequest,
  type BinaryMatchMessage
} from "./match-binary";
export const supportedProtocolVersionSchema = z.literal(PROTOCOL_VERSION);

export const inventorySlotReferenceSchema = z.object({
  area: z.literal("inventory"),
  slot: z.number().int().min(0).max(9)
});

export const gridSlotReferenceSchema = z.object({
  area: z.literal("grid"),
  slot: z.number().int().min(0).max(8)
});

export const slotReferenceSchema = z.discriminatedUnion("area", [
  inventorySlotReferenceSchema,
  gridSlotReferenceSchema
]);

const clientEnvelope = { protocolVersion: supportedProtocolVersionSchema };

export const clientMessageSchema = z.discriminatedUnion("type", [
  z.object({
    ...clientEnvelope,
    type: z.literal("MOVE_ITEM"),
    sequence: z.number().int().positive(),
    from: inventorySlotReferenceSchema,
    to: gridSlotReferenceSchema
  }),
  z.object({ ...clientEnvelope, type: z.literal("REMOVE_ITEM"), sequence: z.number().int().positive(), slot: z.number().int().min(0).max(8) }),
  z.object({ ...clientEnvelope, type: z.literal("FORFEIT") })
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;

export const queueClientMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("QUEUE_JOIN"), protocolVersion: supportedProtocolVersionSchema, ticketId: z.string().uuid() }),
  z.object({ type: z.literal("QUEUE_CANCEL"), protocolVersion: supportedProtocolVersionSchema, ticketId: z.string().uuid() }),
  z.object({ type: z.literal("OFFER_ACCEPT"), protocolVersion: supportedProtocolVersionSchema, ticketId: z.string().uuid(), offerId: z.string().uuid() }),
  z.object({ type: z.literal("OFFER_DECLINE"), protocolVersion: supportedProtocolVersionSchema, ticketId: z.string().uuid(), offerId: z.string().uuid() }),
  z.object({ type: z.literal("QUEUE_RECOVER"), protocolVersion: supportedProtocolVersionSchema, ticketId: z.string().uuid() })
]);

export type QueueClientMessage = z.infer<typeof queueClientMessageSchema>;

export type QueuePresentation = {
  username: string;
  xp?: number;
  cosmetics: {
    wallpaperId: string;
    craftingTableId: string;
    faceplateId: string;
  };
};

export type QueueServerMessage =
  | { type: "QUEUE_ACCEPTED" | "QUEUE_WAITING"; protocolVersion: number; ticketId: string; serverTime: number; joinedAt: number; queueName: string; ticketExpiresAt: number }
  | { type: "MATCH_OFFERED"; protocolVersion: number; ticketId: string; serverTime: number; offerId: string; expiresAt: number; ticketExpiresAt: number; region: string; opponent: QueuePresentation }
  | { type: "MATCH_FOUND"; protocolVersion: number; ticketId: string; playerId: string; serverTime: number; offerId: string; matchId: string; token: string; opponentId: string; opponentRating: number; opponent: QueuePresentation }
  | { type: "OFFER_EXPIRED"; protocolVersion: number; ticketId: string; serverTime: number; offerId: string; ticketExpiresAt: number }
  | { type: "QUEUE_EXPIRED"; protocolVersion: number; ticketId: string; serverTime: number; ticketExpiresAt: number }
  | { type: "QUEUE_CANCELLED"; protocolVersion: number; ticketId: string; serverTime: number }
  | { type: "QUEUE_ERROR"; protocolVersion: number; ticketId: string; serverTime: number; code: string; terminal: boolean; ticketExpiresAt?: number; retryAfterMs?: number };

export const matchModeSchema = z.enum(["casual", "ranked", "private"]);
export type ServerMatchMode = z.infer<typeof matchModeSchema>;

export type PlayerProgressSnapshot = {
  playerId: string;
  xp: number;
  rankedRating: number;
  version: number;
  progressionEligible: boolean;
};

export type MasterCraftResult = {
  playerId: string;
  round: number;
  recipeId: string;
  difficulty: 4 | 5;
  rawElapsedMs: number;
  latencyAdjustmentMs: number;
  adjustedElapsedMs: number;
  thresholdMs: number;
};

export type MatchCompletedEvent = {
  schemaVersion: 4;
  eventId: string;
  matchId: string;
  mode: Exclude<ServerMatchMode, "private">;
  region: string;
  playerOne: PlayerProgressSnapshot;
  playerTwo: PlayerProgressSnapshot;
  winnerId: string | null;
  score: [number, number];
  totalRounds: number;
  completionReason: "completed" | "disconnect" | "match_timeout" | "connection_timeout" | "aborted";
  masterCrafts: MasterCraftResult[];
  intentionalForfeitPlayerId: string | null;
  startedAt: number;
  completedAt: number;
};

export type PlayerResultEvent = {
  schemaVersion: 2;
  eventId: string;
  matchId: string;
  mode: "casual" | "ranked";
  playerId: string;
  opponentId: string;
  outcome: "win" | "loss" | "draw";
  startingXp: number;
  startingRankedRating: number;
  startingVersion: number;
  opponentStartingXp: number;
  opponentStartingRankedRating: number;
  completedAt: number;
  progressionEligible: boolean;
  completionReason: MatchCompletedEvent["completionReason"];
  masterCrafts: MasterCraftResult[];
  intentionallyForfeited: boolean;
};

export type CoinRewardResult = {
  coinBalance: number;
  coinsGranted: number;
  xpMilestoneCoins: number;
  xpMilestones: number[];
  masterCraftCoins: number;
  masterCraftsRecognized: number;
  masterCraftsRewarded: number;
  masterCraftsToday: number;
  masterCraftDailyLimit: number;
};

export type ProgressionProjectionEvent = {
  eventId: string;
  playerId: string;
  xp: number;
  rankedRating: number;
  progressionVersion: number;
  wins: number;
  losses: number;
  coinBalance?: number;
  coinReward?: CoinRewardResult;
  projectedAt: number;
};

export type ProgressionProjectionBatch = {
  kind: "players";
  schemaVersion: 2;
  eventId: string;
  projections: ProgressionProjectionEvent[];
};

export type LegacyProgressionProjectionEvent = ProgressionProjectionEvent & {
  kind: "player";
  schemaVersion: 1;
};

export type LegacyMatchProjectionEvent = {
  kind: "match";
  schemaVersion: 1;
  event: unknown;
};

export type ProjectionEvent = ProgressionProjectionBatch | LegacyProgressionProjectionEvent | LegacyMatchProjectionEvent;

export type ProgressionPreview = {
  eventId: string;
  xp: number;
  rankedRating: number;
  version: number;
  pending: true;
};
